// AI Ghostwriter Pro - How It Works Page
import React from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import { MessageCircle, FileText, PenTool, BookOpen, CheckCircle, Rocket } from 'lucide-react';

const STEPS = [
  {
    num: 1,
    icon: MessageCircle,
    title: 'Share Your Vision',
    description: 'Tell us about your book idea — the genre, audience, tone, and key themes. The more detail you share, the better your book will be.',
    details: ['Book concept & genre', 'Target audience & tone', 'Character ideas or key topics', 'Word count & chapter preferences']
  },
  {
    num: 2,
    icon: FileText,
    title: 'Review Your Outline',
    description: 'Within 24-48 hours, you receive a detailed chapter-by-chapter outline. Review it, request changes, and approve when you\'re satisfied.',
    details: ['Chapter-by-chapter breakdown', 'Character arcs & plot points', 'Key themes per section', 'Revise until you approve']
  },
  {
    num: 3,
    icon: PenTool,
    title: 'AI Writes Your Book',
    description: 'Our AI writes each chapter following your approved outline. Each chapter maintains context awareness — characters, plot threads, and tone stay consistent throughout.',
    details: ['Professional prose quality', 'Context-aware writing', 'Consistent voice & style', 'Real-time progress tracking']
  },
  {
    num: 4,
    icon: CheckCircle,
    title: 'Review & Revise',
    description: 'Read each chapter as it\'s completed. Request revisions on any section. Your package includes revision rounds to ensure you\'re completely happy.',
    details: ['Chapter-by-chapter review', 'Targeted revision requests', 'Quality assurance checks', 'Plagiarism verification']
  },
  {
    num: 5,
    icon: BookOpen,
    title: 'Receive Your Manuscript',
    description: 'Download your complete, publication-ready manuscript. Professional and Complete packages include Amazon-optimized metadata and cover design.',
    details: ['Complete formatted manuscript', 'Amazon-ready description', 'Keyword-optimized metadata', 'Publication-ready files']
  },
  {
    num: 6,
    icon: Rocket,
    title: 'Publish & Earn',
    description: 'Publish on Amazon KDP, Barnes & Noble, or any retailer. You own 100% of the rights and keep 100% of the royalties.',
    details: ['Full copyright ownership', 'Publish anywhere', 'Keep all royalties', 'Launch strategy (Complete pkg)']
  }
];

export function GhostwriterHowItWorks() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SeoHead
        title="How It Works | AI Ghostwriter Pro - From Idea to Published Book"
        description="Learn how AI Ghostwriter Pro turns your book idea into a published manuscript in 6 simple steps. Share your vision, review the outline, and receive your complete book in 7-14 days."
        canonical="https://aighostwriterpro.com/how-it-works"
        og={{ type: 'website', url: 'https://aighostwriterpro.com/how-it-works', title: 'How AI Ghostwriting Works - 6 Simple Steps', description: 'From book idea to published manuscript in 7-14 days.', siteName: 'AI Ghostwriter Pro' }}
        twitter={{ card: 'summary_large_image', title: 'How AI Ghostwriting Works', description: 'From idea to published book in 6 steps.' }}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="how-it-works-heading">
            How It Works
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            From book idea to published manuscript in 6 simple steps. The entire process takes 7-14 days depending on your package.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.num} className="flex gap-6 items-start" data-testid={`step-${step.num}`}>
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-purple-400 text-sm font-mono">Step {step.num}</span>
                    <h3 className="text-xl font-bold text-white">{step.title}</h3>
                  </div>
                  <p className="text-gray-400 mb-3">{step.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {step.details.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" />
                        {d}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <Link to="/pricing">
            <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-3 text-lg" data-testid="how-cta">
              View Packages & Pricing
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default GhostwriterHowItWorks;
